import React, { useState, useEffect, useCallback } from 'react';
import APIService from '../services/api';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';

/**
 * Entretien d'un candidat sur un besoin.
 *
 * L'entretien n'est pas un objet à part : c'est un ÉTAT du couple candidat ×
 * besoin, au même titre que « proposé » ou « retenu ». Il vit donc dans la
 * carte du candidat, là où se prend la décision, et pas dans un module qui
 * dupliquerait le statut.
 *
 * Le candidat choisit son heure sur erpia.ch/entretien/:token — on ne fixe
 * rien à sa place, on propose. D'où l'état intermédiaire « proposé, en
 * attente », qui est le plus fréquent et le plus utile à voir.
 *
 * Routes :
 *   GET  /besoins/:b/candidats/:c/entretien → { statut, creneaux, creneau, … }
 *        (jamais de 404 : « aucun » est une réponse)
 *   POST même route → { statut, creneaux, url, email_envoye, email_erreur?,
 *                       message_utilisateur }
 *   DELETE /entretiens/:id { motif? } → annule et prévient le candidat
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const TYPES = [
  ['qualification', 'Qualification'],
  ['technique', 'Technique'],
  ['client', 'Avec le client'],
];
// QUI MÈNE L'ENTRETIEN — deux valeurs, celles que le serveur accepte.
// C'était un champ de TEXTE LIBRE (placeholder « Vous, par défaut ») : laissé
// vide, l'entretien partait en « humain » et AUCUN appel IA n'était planifié ;
// rempli avec un nom de personne, l'API répondait « appelant invalide ». La
// capacité existait, elle n'était pas atteignable.
const APPELANTS = [
  ['humain', 'Vous'],
  ['agent_ia', "L'agent IA"],
];
const APPELANT_LABEL = Object.fromEntries(APPELANTS);
const quandLong = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};
const quandCourt = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('fr-CH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const META = {
  aucun: ['Aucun entretien', 'muet'],
  propose: ['Créneaux proposés — en attente', 'attente'],
  confirme: ['Entretien confirmé', 'ok'],
  annule: ['Entretien annulé', 'muet'],
};

export default function EntretienCandidat({ besoinId, candidatId, nomCandidat, showNotification, onEtat }) {
  const [etat, setEtat] = useState(null);      // null = chargement
  const [erreur, setErreur] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);      // options d'envoi, ouvertes à la demande
  const [lien, setLien] = useState(null);      // { url, email_envoye, motif }
  const [copie, setCopie] = useState(false);
  // Questions du besoin (onglet Questions) : l'agent IA les pose. Chargées
  // seulement quand on le choisit — inutile de payer un aller-retour sinon.
  // null = pas encore lu, [] = lu et vide (les deux ne se disent pas pareil).
  const [questions, setQuestions] = useState(null);
  const [qCharge, setQCharge] = useState(false);

  const charger = useCallback(async () => {
    if (besoinId == null || candidatId == null) return;
    setErreur('');
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/candidats/${candidatId}/entretien`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const d = j.data ?? j;
      setEtat({ ...d, statut: d.statut || 'aucun' });
      onEtat?.(candidatId, d.statut || 'aucun');
    } catch (e) { setErreur(e.message); setEtat({ statut: 'aucun' }); }
  }, [besoinId, candidatId, onEtat]);
  useEffect(() => { charger(); }, [charger]);

  // Les questions du besoin, telles que l'écran Questions les montre.
  const chargerQuestions = useCallback(async () => {
    if (besoinId == null) return;
    setQCharge(true);
    try {
      const r = await fetch(`${BASE}/tests/besoin/${besoinId}`, { headers: authH() });
      const j = await r.json().catch(() => []);
      const liste = (Array.isArray(j) ? j : (j.questions || []))
        .map(q => String(q?.question ?? q).trim()).filter(Boolean);
      setQuestions(liste);
    } catch { setQuestions([]); }
    finally { setQCharge(false); }
  }, [besoinId]);

  // Choisir « L'agent IA » va chercher son script tout de suite : ce qui
  // manquera au moment de planifier l'appel doit se voir MAINTENANT, avant
  // qu'on fasse choisir un créneau à quelqu'un pour un appel qui ne partira pas.
  const choisirAppelant = (v) => {
    setForm(f => ({ ...f, appelant: v }));
    if (v === 'agent_ia' && questions === null) chargerQuestions();
  };

  const proposer = async () => {
    setBusy(true); setErreur('');
    try {
      const corps = {};
      if (form?.type) corps.type = form.type;
      if (String(form?.appelant || '').trim()) corps.appelant = form.appelant.trim();
      // L'agent IA part avec le script du besoin : sans questions, le serveur
      // refuse (« il décrocherait sans savoir quoi demander ») — autant les
      // joindre, puisqu'elles existent déjà à l'onglet Questions.
      if (form?.appelant === 'agent_ia' && questions?.length) corps.questions = questions;
      if (Number(form?.duree_min) > 0) corps.duree_min = Number(form.duree_min);
      if (String(form?.message || '').trim()) corps.message = form.message.trim();
      const r = await fetch(`${BASE}/besoins/${besoinId}/candidats/${candidatId}/entretien`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const d = j.data ?? j;
      // L'email peut ne pas partir : le lien est alors le seul moyen.
      setLien({ url: d.url || '', email_envoye: d.email_envoye !== false, motif: d.email_erreur || '' });
      setCopie(false);
      if (d.email_envoye === false) {
        showNotification?.(d.email_erreur || 'Les créneaux sont prêts mais l\'email n\'est pas parti — transmettez le lien vous-même.', 'error');
      } else {
        showNotification?.(d.message_utilisateur || j.message || `Créneaux proposés à ${nomCandidat || 'ce candidat'}.`, 'success');
      }
      setForm(null);
      await charger();
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  };

  // Relancer l'appel : l'agent IA rappelle dans une minute, par le même
  // planificateur que le créneau. Un téléphone sonne : on confirme d'abord.
  // CONFIER L'ENTRETIEN À L'AGENT IA — l'entretien est parti « mené par
  // vous » (le défaut), le candidat a choisi son créneau… et personne
  // n'appelle : l'appel n'avait jamais été demandé. On bascule l'entretien
  // EXISTANT, sans l'annuler ni faire rechoisir une heure au candidat.
  const confierAgent = async () => {
    const id = etat?.id ?? etat?.entretien_id;
    if (!id) return;
    const quand = statut === 'confirme'
      ? (creneau && new Date(creneau).getTime() > Date.now()
        ? `au créneau retenu (${quandLong(creneau)})`
        : 'dans quelques minutes — le créneau retenu est déjà passé')
      : 'dès que le candidat aura choisi son créneau';
    const ok = await confirmErpia({
      title: 'Confier l’appel à l’agent IA ?',
      message: `L’agent IA appellera ${nomCandidat || 'le candidat'} ${quand}.`,
      confirmLabel: 'Confier à l’agent IA',
    });
    if (!ok) return;
    setBusy(true); setErreur('');
    try {
      const r = await fetch(`${BASE}/entretiens/${id}/agent-ia`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501) { showNotification?.('Confier l’entretien à l’agent IA arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { setErreur(j.error || `Impossible de confier l’entretien (${r.status})`); return; }
      showNotification?.(j.message || 'Entretien confié à l’agent IA.', 'success');
      await charger();
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  };

  // `maintenant` : avancer un appel DÉJÀ planifié (le créneau retenu est
  // décroché, un seul appel part). Sans lui, un appel planifié est laissé
  // tranquille — c'est le cas normal, il partira tout seul.
  const relancer = async ({ maintenant = false } = {}) => {
    const id = etat?.id ?? etat?.entretien_id;
    if (!id) return;
    const ok = await confirmErpia({
      title: maintenant ? 'Appeler maintenant ?' : appel?.statut === 'passe' ? 'Rappeler le candidat ?' : 'Relancer l’appel ?',
      message: `L’agent IA appellera ${nomCandidat || 'le candidat'} dans une minute`
        + (maintenant && creneau ? ` — le créneau du ${quandLong(creneau)} est décroché, il n’y aura qu’un seul appel.`
          : appel?.statut === 'passe' ? ' — un appel a déjà eu lieu.' : '.'),
      confirmLabel: maintenant ? 'Appeler maintenant' : appel?.statut === 'passe' ? 'Rappeler' : 'Relancer l’appel',
    });
    if (!ok) return;
    setBusy(true); setErreur('');
    try {
      const r = await fetch(`${BASE}/entretiens/${id}/appel`, { method: 'POST', headers: jsonH(),
        body: JSON.stringify({ dans_min: 1, ...(maintenant ? { maintenant: true } : {}) }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501) { showNotification?.('La relance de l’appel arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { setErreur(j.error || `Relance impossible (${r.status})`); return; }
      showNotification?.(j.message || 'Appel relancé.', 'success');
      await charger();
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  };

  const annuler = async () => {
    const id = etat?.id ?? etat?.entretien_id;
    if (id == null) { showNotification?.('Cet entretien n\'a pas d\'identifiant — rechargez la fiche.', 'error'); return; }
    const ok = await confirmErpia({
      title: 'Annuler l\'entretien',
      message: etat.statut === 'confirme'
        ? `${nomCandidat || 'Le candidat'} sera prévenu que l'entretien du ${quandLong(etat.creneau?.debut || etat.creneau) || 'créneau retenu'} est annulé.`
        : `${nomCandidat || 'Le candidat'} sera prévenu que la proposition est retirée, et son lien cessera de fonctionner.`,
      detail: 'Vous pourrez proposer de nouveaux créneaux ensuite.',
      confirmLabel: 'Annuler l\'entretien', tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/entretiens/${id}`, { method: 'DELETE', headers: jsonH(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Entretien annulé — le candidat est prévenu.', 'success');
      setLien(null);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (etat === null) return <div className="entc entc--vide"><BI icon="arrow-repeat" /> Lecture de l'entretien…</div>;

  const statut = etat.statut || 'aucun';
  const [libelle, ton] = META[statut] || META.aucun;
  const creneau = etat.creneau?.debut || etat.creneau;
  const creneaux = Array.isArray(etat.creneaux) ? etat.creneaux : [];
  // L'état RÉEL de l'appel de l'agent IA, tel que le serveur le lit sur son
  // planificateur (`appel`). Un serveur d'avant ce contrat ne le donne pas :
  // on retombe alors sur « une ligne existe / n'existe pas ».
  const appel = etat.appel || (etat.appelant === 'agent_ia' && statut === 'confirme'
    ? (etat.scheduled_call_id ? { statut: 'planifie' } : { statut: 'non_planifie', erreur: etat.appel_erreur || null })
    : null);
  const appelRelancable = !!appel && ['manque', 'echoue', 'non_planifie', 'annule', 'passe'].includes(appel.statut);

  return (
    <div className="entc" data-ton={ton}>
      <div className="entc-tete">
        <BI icon={statut === 'confirme' ? 'calendar-check' : statut === 'propose' ? 'hourglass-split' : 'calendar-event'} />
        <span className="entc-libelle">{libelle}</span>
        {statut === 'confirme' && creneau && <b className="entc-quand">{quandLong(creneau)}</b>}
        {etat.duree_min ? <span className="entc-duree">{etat.duree_min} min</span> : null}
        {etat.appelant ? <span className="entc-duree">avec {APPELANT_LABEL[etat.appelant] || etat.appelant}</span> : null}
        <span className="entc-espace" />
        {(statut === 'aucun' || statut === 'annule') && !form && (
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setForm({ type: '', appelant: 'humain', duree_min: '', message: '' })}>
            <BI icon="calendar-plus" /> Proposer un entretien
          </button>
        )}
        {(statut === 'propose' || statut === 'confirme') && (
          <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" onClick={annuler} disabled={busy}>Annuler</button>
        )}
      </div>

      {erreur && <div className="entc-erreur" role="alert">{erreur}</div>}

      {/* L'appel de l'agent IA : ce qu'il est VRAIMENT devenu. « Planifié »
          ne suffit pas — un appel manqué par le serveur ou échoué se lit ici,
          avec sa raison, et se relance d'un geste. */}
      {statut === 'confirme' && appel && (
        <div className={`entc-appel entc-appel--${appel.statut}`} role={appelRelancable && appel.statut !== 'passe' ? 'alert' : undefined}>
          <BI icon={{ planifie: 'telephone-outbound', en_cours: 'telephone-inbound', passe: 'telephone-check', manque: 'telephone-x', echoue: 'telephone-x', non_planifie: 'telephone-x', annule: 'telephone-minus' }[appel.statut] || 'telephone'} />
          <span className="entc-appel-texte">
            {appel.statut === 'planifie' && `L’agent IA appellera tout seul ${creneau ? `le ${quandLong(creneau)}` : 'à l’heure du créneau'}.`}
            {appel.statut === 'en_cours' && 'L’agent IA est en ligne avec le candidat.'}
            {appel.statut === 'passe' && 'L’appel de l’agent IA a eu lieu.'}
            {appel.statut === 'manque' && <>L’appel de l’agent IA n’est pas parti{appel.erreur ? <> : {appel.erreur}</> : ''}.</>}
            {appel.statut === 'echoue' && <>L’appel de l’agent IA a échoué{appel.erreur ? <> : {appel.erreur}</> : ''}.</>}
            {appel.statut === 'non_planifie' && <>L’appel de l’agent IA n’a pas pu être planifié{appel.erreur ? <> : {appel.erreur}</> : ''}.</>}
            {appel.statut === 'annule' && 'L’appel de l’agent IA a été annulé.'}
          </span>
          {appelRelancable && (
            <button type="button" className={`erp-btn erp-btn--sm ${appel.statut === 'passe' ? 'erp-btn--secondary' : 'erp-btn--primary'}`} onClick={() => relancer()} disabled={busy}>
              <BI icon="arrow-repeat" /> {appel.statut === 'passe' ? 'Rappeler' : 'Relancer l’appel'}
            </button>
          )}
          {/* Le créneau le plus proche est à 24 h : sans cette porte, tester
              l'appel ou avancer un rendez-vous obligeait à annuler l'entretien
              et à faire rechoisir une heure au candidat. */}
          {appel.statut === 'planifie' && (
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => relancer({ maintenant: true })} disabled={busy}>
              <BI icon="telephone-outbound" /> Appeler maintenant
            </button>
          )}
        </div>
      )}

      {/* Entretien mené par un humain : l'agent IA n'appellera PAS — et il
          faut le dire, sinon on attend un appel que personne n'a demandé.
          Le bouton bascule l'entretien EXISTANT, sans l'annuler ni faire
          rechoisir une heure au candidat. */}
      {(statut === 'propose' || statut === 'confirme') && etat.appelant !== 'agent_ia' && (
        <div className="entc-appel entc-appel--humain">
          <BI icon="person-workspace" />
          <span className="entc-appel-texte">
            Cet entretien est mené par vous : l’agent IA n’appellera pas.
          </span>
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={confierAgent} disabled={busy}>
            <BI icon="telephone-outbound" /> Confier l’appel à l’agent IA
          </button>
        </div>
      )}

      {statut === 'propose' && creneaux.length > 0 && (
        <p className="entc-creneaux">
          {creneaux.length} créneau{creneaux.length > 1 ? 'x' : ''} proposé{creneaux.length > 1 ? 's' : ''} :{' '}
          <span className="erp-mono">{creneaux.slice(0, 4).map(c => quandCourt(c.debut || c)).filter(Boolean).join(' · ')}</span>
          {creneaux.length > 4 ? ` +${creneaux.length - 4}` : ''}
        </p>
      )}

      {lien && (
        <div className="entc-lien" data-ton={lien.email_envoye ? 'ok' : 'alerte'} role="status">
          <BI icon={lien.email_envoye ? 'link-45deg' : 'exclamation-triangle-fill'} />
          <div>
            <strong>{lien.email_envoye ? 'Lien envoyé au candidat' : 'Lien prêt — l\'email n\'est pas parti'}</strong>
            {!lien.email_envoye && <span className="entc-lien-motif">{lien.motif || 'Transmettez ce lien vous-même.'}</span>}
            <code className="erp-mono">{lien.url || '—'}</code>
          </div>
          {lien.url && (
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm"
              onClick={async () => { try { await navigator.clipboard.writeText(lien.url); setCopie(true); } catch { showNotification?.('Copie impossible — sélectionnez le lien à la main.', 'error'); } }}>
              {copie ? 'Copié' : 'Copier'}
            </button>
          )}
        </div>
      )}

      {form && (
        <div className="entc-form">
          <p className="entc-form-aide">
            Les créneaux sont fabriqués depuis vos disponibilités (Config → Entretiens).
            Le candidat choisit son heure — vous ne fixez rien à sa place.
          </p>
          <div className="entc-form-grille">
            <label>Type
              <ErpiaSelect value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} aria-label="Type d'entretien">
                <option value="">Par défaut</option>
                {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </ErpiaSelect>
            </label>
            <label>Qui mène l'entretien
              <ErpiaSelect value={form.appelant} onChange={e => choisirAppelant(e.target.value)} aria-label="Qui mène l'entretien">
                {APPELANTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </ErpiaSelect>
            </label>
            <label>Durée (min)
              <input className="erp-field erp-mono" type="number" min="5" step="5" value={form.duree_min} placeholder="Celle des réglages"
                onChange={e => setForm(f => ({ ...f, duree_min: e.target.value }))} />
            </label>
          </div>
          <label className="entc-form-message">Message au candidat
            <textarea className="erp-field" rows={2} value={form.message} placeholder="Ajouté à l'email d'invitation — facultatif."
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
          </label>
          {/* CE QUI MANQUERA À L'APPEL SE DIT MAINTENANT — pas après avoir
              fait choisir un créneau à quelqu'un pour un appel qui ne
              partirait pas. */}
          {form.appelant === 'agent_ia' && (
            qCharge ? (
              <p className="entc-form-aide"><BI icon="arrow-repeat" /> Lecture des questions du besoin…</p>
            ) : questions?.length ? (
              <p className="entc-form-aide">
                L'agent IA posera les <b>{questions.length} question{questions.length > 1 ? 's' : ''}</b> du besoin,
                et appellera au créneau que le candidat aura choisi.
              </p>
            ) : (
              <div className="entc-lien" data-ton="alerte" role="status">
                <BI icon="exclamation-triangle-fill" />
                <div>
                  <strong>Ce besoin n'a aucune question</strong>
                  <span className="entc-lien-motif">
                    L'agent IA décrocherait sans savoir quoi demander. Ajoutez-les dans l'onglet
                    « Questions » du besoin, ou faites l'entretien vous-même.
                  </span>
                </div>
              </div>
            )
          )}
          <div className="entc-form-actions">
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setForm(null)}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={proposer}
              disabled={busy || (form.appelant === 'agent_ia' && (qCharge || !questions?.length))}
              title={form.appelant === 'agent_ia' && !qCharge && !questions?.length
                ? 'Ajoutez des questions au besoin pour que l\'agent IA puisse mener l\'entretien'
                : undefined}>
              {busy ? 'Envoi…' : 'Envoyer les créneaux'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
